import React from 'react';
import SearchHeader from 'v2/common/SearchHeader/SearchHeader';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import Pagination from 'v2/common/Pagination/Pagination';
import {
  FetchSearchesParams,
  GenericObject,
  ReviewTabEnum,
  ReviewTabType,
  SearchEntity,
  SearchStateProps,
} from 'v2/models';
import {
  Columns,
  searchViewPath,
  TypeOfColumn,
} from 'v2/utils/searchViewUtils';
import useFetchSearchesPaginated from 'v2/hooks/useFetchSearchesPaginated';
import useGetPageNumber, {
  useGetPageNumberProps,
} from 'v2/hooks/useGetPageNumber';
import SearchList from 'v2/common/SearchList/SearchList';
import { SearchItem } from 'v2/common/SearchList/SearchItem/SearchItem';
import SearchSpinner from 'v2/common/SearchViewComponents/SearchSpinner';
import NoSearchesMessageText from 'v2/common/SearchViewComponents/NoSearchesMessageText';
import useQueryParams from 'v2/hooks/useQueryParams';
import ReviewTabs, { defaultTab } from './SubTabs/ReviewTabs';
import useGetForReviewTab from 'v2/hooks/useGetPageTabForReview';

const ForReview = ({ user, unreadMessages }: SearchStateProps) => {
  const pageParams: useGetPageNumberProps = {
    onNotValidPageNumber: changePage,
  };
  const history = useHistory();
  const location = useLocation();
  const page = useGetPageNumber(pageParams);
  const tab = useGetForReviewTab() || defaultTab;
  const pageTitle = 'Requests in Review';
  const queryParams = useQueryParams();
  const sortColumn = queryParams.get('sort') || '-last_internal_update';

  const forReviewSearchesParams: FetchSearchesParams = {
    [tab === ReviewTabEnum.actioned ? 'review_actioned' : 'needs_review']: true,
    active: true,
    page,
    ordering: sortColumn,
  };

  const {
    isFetching: forReviewSearchesFetching,
    data: { results: forReviewSearches, count: forReviewSearchesCount } = {},
    isError: forReviewSearchesIsError,
    refetch: forReviewSearchesRefetch,
    error,
  } = useFetchSearchesPaginated(forReviewSearchesParams, [
    'inReviewList',
    String(tab),
    String(page),
    String(queryParams),
  ]);

  const columns: Columns = [
    {
      type: TypeOfColumn.titleDetailed,
      label: 'Title',
    },
    {
      type: TypeOfColumn.originatorDetailed,
      label: 'Originator',
      disabled: !!user.single_person_organization,
    },
    {
      type: TypeOfColumn.conversations,
      label: 'Conversations',
      conversationCount: unreadMessages,
    },
    {
      type: TypeOfColumn.lastActivity,
      label: 'Last Activity',
      sortable: true,
      ascField: 'last_internal_update',
      descField: '-last_internal_update',
    },
  ];

  function handleTabChange(tab: ReviewTabType['eventKey']) {
    history.push({
      pathname: `${searchViewPath.forReview}/${tab}/1`,
    });
  }

  function changePage(page: number) {
    history.push({
      pathname: `${searchViewPath.forReview}/${tab}/${page}`,
      search: location.search,
    });
    window.scrollTo(0, 0);
  }

  const handleSort = (sort: string) => {
    const params = new URLSearchParams({ sort });
    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  if (forReviewSearchesIsError) {
    if (error === 'Invalid page.') {
      changePage(1);
    }
    return <div data-testid="errorFetching"></div>;
  }

  const renderSearchList = () => {
    if (forReviewSearches) {
      return (
        <>
          <SearchList
            className="for-review"
            columns={columns}
            data={forReviewSearches}
            currentSortField={sortColumn}
            onSortChange={handleSort}
            renderSearchItem={(search: SearchEntity, columns: Columns) => {
              return (
                <SearchItem
                  refetch={forReviewSearchesRefetch}
                  showForReviewButton={true}
                  columns={columns}
                  search={search}
                  key={search.id}
                />
              );
            }}
          />
          <Pagination
            totalCountOfItems={forReviewSearchesCount}
            callBack={(pageSelected) => {
              changePage(pageSelected);
            }}
            currentPageOfItems={page}
          />
        </>
      );
    }
  };

  const renderForReviewSearches = () => {
    if (forReviewSearchesFetching) {
      return <SearchSpinner />;
    } else if (!forReviewSearches || !forReviewSearches?.length) {
      return (
        <div className="mt-5">
          <NoSearchesMessageText title="Hurray! You have no requests to review." />
        </div>
      );
    } else {
      return renderSearchList();
    }
  };

  return (
    <div className={classnames('fix-bg-images w-100')}>
      <span aria-level={1}>
        <SearchHeader pageTitle={pageTitle} />
      </span>
      <div className="container-xl">
        <ReviewTabs currentTab={tab} onTabChange={handleTabChange} />
        {renderForReviewSearches()}
      </div>
    </div>
  );
};

const mapStateToProps = (state: GenericObject): SearchStateProps => {
  return {
    user: state.user.user,
    unreadMessages: state.chat.unreadMessages,
  };
};

export default connect<SearchStateProps>(mapStateToProps)(ForReview);
